/* ###########################################################################
// Nav
//
// Owns everything interactive about the header pill declared in
// _components/nav.twig + _components/navItem.twig: dropdowns (desktop
// flyouts and mobile accordions share one handler), the mobile panel, the
// sliding hover indicator, the hidden state of the pill (slides away on
// scroll down, back on scroll up), the scrolled state (transparent at rest,
// frosted glass once scrolled), and the on-dark/on-light colour swap.
//
// Markup is plain Tailwind utility classes - there's no companion CSS file.
// Every animated state is a GSAP tween/timeline here; the scrolled look is
// still just Tailwind classes toggled on and off, since a CSS transition
// already handles those smoothly.
//######################################################################### */

import gsap from 'gsap'
import { throttle } from '../utilities/throttle'
import { disableScroll, enableScroll } from '../utilities/scrollLock'

const nav = {
    config: {
        nav: '[data-nav]',
        navBackdrop: '[data-nav-backdrop]',
        navList: '[data-nav-list]',
        navIndicator: '[data-nav-indicator]',
        navPanel: '[data-nav-panel]',
        navMenuBtn: '[data-nav-menu-btn]',
        navMenuIconOpen: '[data-nav-menu-icon-open]',
        navMenuIconClose: '[data-nav-menu-icon-close]',
        navRow: '[data-nav-row]',
        navLogo: '[data-nav-logo]',
        navItem: '[data-nav-item]',
        navSubMenu: '[data-nav-submenu]',
        navTrigger: '[data-nav-trigger]',
        navTopLink: '[data-nav-top-link]',
        navChevron: '[data-nav-chevron]',
        navSubMenuTail: '[data-nav-submenu-tail]',
        panelLinks: '[data-nav-panel-links] > [data-nav-item]',
        panelCta: '[data-nav-panel-cta]',
        panelFade: '[data-nav-panel-fade]',
        panelPop: '[data-nav-panel-pop]',
    },

    elements: {},

    state: {
        isMobile: false,
        menuOpen: false,
        scrolled: false,
        hidden: false,
        reducedMotion: false,
    },

    // Matches Tailwind's `lg`, which is where the twig swaps the desktop list
    // for the mobile panel.
    breakpoint: 1024,

    // Scroll tuning. `hideDistance`/`revealDistance` measure distance travelled
    // in the current direction rather than a single event's delta: a trackpad
    // fires many events of a pixel or two, so comparing one event at a time to
    // a tolerance meant "scrolling down" almost never registered.
    scrollThreshold: 24,
    hideDistance: 80,
    revealDistance: 10,

    // Minimum gap a desktop top-level flyout must keep from the viewport
    // edge - centring under the trigger (xPercent: -50) overflows off-screen
    // for items near either end of the pill, so this is clamped in on open.
    edgeGutter: 16,

    init() {
        const self = this

        self.elements.nav = document.querySelector(self.config.nav)

        if (self.elements.nav === null) return

        self.state.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        self.state.navOnDark = self.elements.nav.dataset.navOnDark === '1'

        self.elements.navBackdrop = document.querySelector(self.config.navBackdrop)
        self.elements.navRow = self.elements.nav.querySelector(self.config.navRow)
        self.elements.navList = self.elements.nav.querySelector(self.config.navList)
        self.elements.navIndicator = self.elements.nav.querySelector(self.config.navIndicator)
        // Queried from the document, not scoped to `nav` - the panel is a
        // sibling of `data-nav` in the markup (see the comment on it in
        // nav.twig), same as navBackdrop below.
        self.elements.navPanel = document.querySelector(self.config.navPanel)
        // Everything outside this - `main`, the footer, etc. - goes `inert`
        // while the mobile panel is open (see setBodyInert), so it's found
        // once here rather than re-querying the DOM on every open/close.
        self.elements.header = self.elements.nav.closest('header')
        self.elements.navMenuBtn = self.elements.nav.querySelector(self.config.navMenuBtn)
        self.elements.navMenuIconOpen = self.elements.nav.querySelector(self.config.navMenuIconOpen)
        self.elements.navMenuIconClose = self.elements.nav.querySelector(self.config.navMenuIconClose)
        self.elements.navLogo = self.elements.nav.querySelector(self.config.navLogo)
        // Queried from the document, not scoped to `nav` - the mobile panel's
        // copy of the tree (scope: 'panel' in navItem.twig) is a sibling of
        // `data-nav`, same as navPanel/navBackdrop above, so scoping these to
        // `self.elements.nav` would miss every trigger/submenu/chevron
        // rendered inside it, leaving the mobile accordion permanently
        // un-collapsed and unclickable.
        self.elements.navSubMenus = document.querySelectorAll(self.config.navSubMenu)
        self.elements.navTriggers = document.querySelectorAll(self.config.navTrigger)
        self.elements.navChevrons = document.querySelectorAll(self.config.navChevron)
        // navTopLinks stays scoped to the pill: it drives the on-dark/on-light
        // theme swap, which only applies to the desktop/pill copy - the mobile
        // panel's top links are always dark-on-white regardless of hero theme
        // (see topLinkClasses in navItem.twig), so theming the panel's copies
        // too would turn them white-on-white when the hero theme is light.
        self.elements.navTopLinks = self.elements.nav.querySelectorAll(self.config.navTopLink)

        self.state.isMobile = window.innerWidth < self.breakpoint

        // Every submenu starts closed and, on desktop, positioned relative to
        // the item that opens it - set once and left alone, so open/close only
        // ever has to tween opacity/visibility (+ height on mobile, where it
        // also starts collapsed).
        self.elements.navSubMenus.forEach((subMenu) => {
            gsap.set(subMenu, { autoAlpha: 0, y: -8 })
            self.setSubMenuTabIndexes(subMenu, false)
        })

        self.applySubMenuBreakpointState()

        // Chevrons rest pointing down at the top level and sideways once
        // nested (level 3+ flies out to the side) - see the
        // `data-nav-chevron-base` attribute navItem.twig sets on each one.
        self.elements.navChevrons.forEach((chevron) => {
            gsap.set(chevron, { rotate: Number(chevron.dataset.navChevronBase || 0) })
        })

        gsap.set(self.elements.navPanel, { autoAlpha: 0, y: -8 })
        gsap.set(self.elements.navBackdrop, { autoAlpha: 0 })
        gsap.set(self.elements.navMenuIconClose, { autoAlpha: 0, rotate: -90 })

        // The pill starts hidden via this inline state rather than a
        // permanent Tailwind class, so playEntrance()'s tween has a real "from"
        // to animate away from instead of reading an already-zero opacity.
        gsap.set(self.elements.nav, { autoAlpha: 0, y: -16 })

        self.handleScroll()
        self.handleMenuBtn()
        self.handleSubMenuItems()
        self.handleSubMenuFocusOut()
        self.handleBackdropClick()
        self.handleIndicator()
        self.handleKeydown()
        self.handleOutsideClick()

        if (self.state.isMobile) self.setNavHeight()

        window.addEventListener('resize', throttle(() => {
            const wasMobile = self.state.isMobile

            self.state.isMobile = window.innerWidth < self.breakpoint

            if (self.state.isMobile === wasMobile) return

            // Crossing the breakpoint leaves state stranded either way - a
            // desktop flyout mid-open has no meaning as a mobile accordion,
            // and vice versa - so close everything and reset the height
            // handling for whichever mode we're entering.
            self.resetMenuState()

            if (self.state.isMobile) {
                self.setNavHeight()
            } else {
                self.unsetNavHeight()
            }

            self.applySubMenuBreakpointState()
        }, 250))

        self.playEntrance()
    },

    // Duration helper - collapses every animated duration to (near) zero when
    // the visitor has asked for reduced motion, rather than branching every
    // call site.
    dur(seconds) {
        return this.state.reducedMotion ? 0.001 : seconds
    },

    // Desktop centres top-level dropdowns under their trigger with a GSAP
    // `xPercent`; mobile lays the same element out in normal flow, where that
    // transform would just shift it sideways off past the edge of the
    // viewport. Mobile also starts every submenu collapsed (height: 0) for
    // the accordion; desktop doesn't constrain height at all.
    applySubMenuBreakpointState() {
        const self = this

        self.elements.navSubMenus.forEach((subMenu) => {
            const nested = subMenu.dataset.navSubmenuNested === '1'

            if (self.state.isMobile) {
                gsap.set(subMenu, { xPercent: 0, height: 0 })
            } else {
                gsap.set(subMenu, { xPercent: nested ? 0 : -50, height: 'auto', clearProps: 'height' })
            }
        })
    },

    /* Entrance
    //------------------*/

    playEntrance() {
        const self = this
        const topItems = self.elements.navList
            ? self.elements.navList.querySelectorAll(':scope > [data-nav-item]')
            : []

        gsap.timeline({ defaults: { ease: 'power3.out' } })
            .to(self.elements.nav, { autoAlpha: 1, y: 0, duration: self.dur(0.6) })
            .from(topItems, {
                autoAlpha: 0,
                y: -8,
                duration: self.dur(0.4),
                stagger: self.dur(0.06),
            }, '-=0.3')
    },

    /* Scroll
    //------------------*/

    handleScroll() {
        const self = this

        let lastScrollY = Math.max(window.scrollY, 0)
        let runDirection = 0
        let runStartY = lastScrollY

        const onScroll = () => {
            const currentY = Math.max(window.scrollY, 0)
            const delta = currentY - lastScrollY
            lastScrollY = currentY

            self.setScrolled(currentY > self.scrollThreshold)

            // Near the top there's nothing to gain from hiding, and while the
            // panel is open the toggle must stay on screen to close it again.
            if (currentY <= self.scrollThreshold || self.state.menuOpen) {
                self.setHidden(false)
                runDirection = 0
                runStartY = currentY

                return
            }

            const direction = delta > 0 ? 1 : delta < 0 ? -1 : runDirection

            if (direction !== runDirection) {
                runDirection = direction
                runStartY = currentY - delta
            }

            const travelled = Math.abs(currentY - runStartY)

            if (runDirection > 0 && travelled > self.hideDistance) {
                self.setHidden(true)
            } else if (runDirection < 0 && travelled > self.revealDistance) {
                self.setHidden(false)
            }
        }

        window.addEventListener('scroll', throttle(onScroll, 100), { passive: true })

        onScroll()
    },

    // Transparent at rest, white frosted glass once scrolled - one look
    // regardless of the hero's theme (not tinted dark/light to match it, the
    // way this used to work), so the text switches to navy on scroll instead
    // (see updateTheme) the same way it would sitting on any other white
    // surface on the site.
    setScrolled(next) {
        const self = this

        if (self.state.scrolled === next) return

        self.state.scrolled = next

        self.applyScrolledLook()
        self.updateTheme()
    },

    // Split out from setScrolled so setMenuOpen can re-run it too: the look
    // reflects `scrolled`, except while the mobile panel is open, where it's
    // forced back to transparent regardless. The panel already has its own
    // solid white sheet directly beneath the pill, so a coloured pill
    // sitting on top of that fights it rather than blending in - however far
    // down the page the visitor had scrolled to open it.
    applyScrolledLook() {
        const self = this
        const on = self.state.scrolled && !self.state.menuOpen

        self.elements.nav.classList.toggle('backdrop-blur-xl', on)
        // A soft, diffuse shadow carries the elevation instead of a hard
        // border now that the glass is always white - `shadow-lg`'s tighter,
        // darker shadow read as a harder edge than the rest of this pill's
        // rounded, translucent look.
        self.elements.nav.classList.toggle('shadow-[0_20px_50px_-15px_rgba(0,44,62,0.25)]', on)
        self.elements.nav.classList.toggle('bg-white/90', on)
        self.elements.navRow?.classList.toggle('px-4', on)
        self.elements.navRow?.classList.toggle('lg:px-6', on)
    },

    setHidden(next) {
        const self = this

        if (self.state.hidden === next) return

        self.state.hidden = next

        // A fixed `yPercent` sized off the pill's own height isn't enough to
        // clear it - the pill sits offset from the header's top edge by the
        // wrapping container's `mt-4`/`md:mt-8`, so translating up by only
        // the pill's own height leaves that gap's worth of it still poking
        // onto screen. Measuring the actual distance to the header's top
        // edge (plus a few px for the shadow's blur) clears it regardless of
        // that margin or the pill's height at the current breakpoint.
        const clearance = self.elements.nav.offsetTop + self.elements.nav.offsetHeight + 16

        // The toggle must stay reachable while the mobile panel is open, so
        // hiding never applies then - resetMenuState/setMenuOpen keep this
        // flag in sync rather than re-checking here.
        gsap.to(self.elements.nav, {
            y: next ? -clearance : 0,
            duration: self.dur(0.3),
            ease: 'power2.out',
        })
    },

    /* On-dark / on-light theming
    //------------------*/

    // Top-level content (logo, top links, menu button, indicator) follows the
    // hero's theme at rest, but always goes navy once the pill itself has
    // gone white - scrolled, or with the mobile panel open (always on a
    // white sheet, regardless of the hero underneath).
    updateTheme() {
        const self = this
        const light = self.state.navOnDark && !self.state.scrolled && !self.state.menuOpen

        const themed = [self.elements.navLogo, self.elements.navMenuBtn, ...self.elements.navTopLinks]

        themed.forEach((el) => {
            if (!el) return

            el.classList.toggle('text-white', light)
            el.classList.toggle('text-ftfblue', !light)
        })

        if (self.elements.navIndicator) {
            self.elements.navIndicator.classList.toggle('bg-white/15', light)
            self.elements.navIndicator.classList.toggle('bg-ftfgrey', !light)
        }
    },

    /* Mobile panel sizing
    //------------------*/

    // The panel is a fixed, full-viewport sheet sitting behind the pill (the
    // pill's row stays on top via z-index so the logo/close button remain
    // usable) - this pushes its content down clear of the row rather than
    // letting it sit underneath, and lets the content scroll within the
    // panel if it's taller than the remaining viewport.
    setNavHeight() {
        const self = this

        if (!self.elements.navPanel || !self.elements.navRow) return

        const offset = self.elements.navRow.getBoundingClientRect().bottom

        self.elements.navPanel.style.paddingTop = `${offset + 16}px`
    },

    unsetNavHeight() {
        const self = this

        if (!self.elements.navPanel) return

        self.elements.navPanel.style.paddingTop = ''
    },

    /* Menu button
    //------------------*/

    handleMenuBtn() {
        const self = this

        if (!self.elements.navMenuBtn) return

        self.elements.navMenuBtn.addEventListener('click', (e) => {
            e.preventDefault()

            self.setMenuOpen(!self.state.menuOpen)
        })
    },

    setMenuOpen(open) {
        const self = this

        if (self.state.menuOpen === open) return

        self.state.menuOpen = open

        self.elements.navMenuBtn?.setAttribute('aria-expanded', open ? 'true' : 'false')

        const label = self.elements.navMenuBtn?.querySelector('[data-menu-btn-label]')

        if (label) label.textContent = open ? 'Close main menu' : 'Open main menu'

        gsap.to(self.elements.navMenuIconOpen, { autoAlpha: open ? 0 : 1, rotate: open ? 90 : 0, duration: self.dur(0.3) })
        gsap.to(self.elements.navMenuIconClose, { autoAlpha: open ? 1 : 0, rotate: open ? 0 : -90, duration: self.dur(0.3) })

        gsap.to(self.elements.navPanel, {
            autoAlpha: open ? 1 : 0,
            y: open ? 0 : -8,
            duration: self.dur(0.3),
            onStart: () => {
                if (open) self.elements.navPanel.classList.remove('pointer-events-none')
            },
            onComplete: () => {
                if (!open) self.elements.navPanel.classList.add('pointer-events-none')
            },
        })

        self.setBackdropOpen(open || self.hasOpenSubMenu())
        self.updateTheme()
        self.applyScrolledLook()

        self.setBodyInert(open)

        if (open) {
            self.setNavHeight()
            self.setHidden(false)
            disableScroll()
            self.playPanelEntrance()
        } else {
            enableScroll()
            self.closeSubMenus()
            self.panelTimeline?.kill()
        }
    },

    // Slides the panel's contents in each time it opens: the links and the
    // CTA from the left, the contact details fading in, then the social icons
    // popping in with a small overshoot. `fromTo` (not `from`) so the starting
    // state is re-applied on every open instead of only the first, and so a
    // timeline killed mid-run on close can't leave anything half-visible next
    // time. Nothing is animated on close - the panel's own fade-out covers it.
    playPanelEntrance() {
        const self = this
        const panel = self.elements.navPanel

        if (!panel) return

        const links = panel.querySelectorAll(self.config.panelLinks)
        const cta = panel.querySelectorAll(self.config.panelCta)
        const fades = panel.querySelectorAll(self.config.panelFade)
        const pops = panel.querySelectorAll(self.config.panelPop)

        self.panelTimeline?.kill()

        self.panelTimeline = gsap.timeline({ delay: self.dur(0.05) })
            .fromTo(links, { autoAlpha: 0, x: -24 }, {
                autoAlpha: 1,
                x: 0,
                duration: self.dur(0.45),
                ease: 'power3.out',
                stagger: self.dur(0.05),
            })
            .fromTo(cta, { autoAlpha: 0, x: -24 }, {
                autoAlpha: 1,
                x: 0,
                duration: self.dur(0.45),
                ease: 'power3.out',
            }, '-=0.25')
            .fromTo(fades, { autoAlpha: 0 }, {
                autoAlpha: 1,
                duration: self.dur(0.4),
                ease: 'power1.out',
                stagger: self.dur(0.06),
            }, '-=0.2')
            // The icons grow past full size and bounce back down to it. Opacity
            // gets its own tween: sharing the elastic ease would make it
            // overshoot and wobble too.
            .fromTo(pops, { autoAlpha: 0 }, {
                autoAlpha: 1,
                duration: self.dur(0.3),
                ease: 'power1.out',
                stagger: self.dur(0.12),
            }, '-=0.2')
            .fromTo(pops, { scale: 0.4 }, {
                scale: 1,
                duration: self.dur(0.8),
                ease: 'elastic.out(1, 0.5)',
                stagger: self.dur(0.12),
            }, '<')
    },

    // Makes everything outside the header - `main`, the footer, the "back to
    // top" button, whatever else lives on the page - unreachable by Tab and
    // invisible to a screen reader's virtual cursor while the mobile panel
    // covers the screen, the same way a native modal dialog would. `inert`
    // does this in one attribute rather than a hand-rolled Tab-key focus
    // trap, and (unlike `aria-hidden` alone) also blocks pointer interaction
    // with anything the backdrop doesn't visually cover.
    setBodyInert(inert) {
        const self = this

        Array.from(document.body.children).forEach((el) => {
            if (el === self.elements.header) return

            el.inert = inert
        })
    },

    /* Submenus
    //------------------*/

    getSubMenu(trigger) {
        return trigger.closest(this.config.navItem)?.querySelector(`:scope > ${this.config.navSubMenu}`) ?? null
    },

    hasOpenSubMenu() {
        return [...this.elements.navSubMenus].some((subMenu) => subMenu.dataset.navOpen === '1')
    },

    handleSubMenuItems() {
        const self = this

        self.elements.navTriggers.forEach((trigger) => {
            const subMenu = self.getSubMenu(trigger)

            if (!subMenu) return

            trigger.addEventListener('click', (e) => {
                e.preventDefault()
                e.stopPropagation()

                const item = trigger.closest(self.config.navItem)
                const isOpen = subMenu.dataset.navOpen === '1'

                // Close siblings first, but never an ancestor or descendant of
                // the item being opened - otherwise opening a level-3 flyout
                // would collapse the panel it lives inside.
                self.closeSubMenus(isOpen ? null : item)

                if (!isOpen) self.positionSubMenu(subMenu)

                self.setSubMenuOpen(trigger, item, subMenu, !isOpen)
            })
        })
    },

    // Top-level flyouts are centred under their trigger via xPercent: -50 in
    // applySubMenuBreakpointState - fine in the middle of the pill, but an
    // item near either end (e.g. the last item, "Careers") centres a wide
    // panel straight off the edge of the viewport. Re-measured on every open
    // (not just once) since window width and which item opened both change.
    positionSubMenu(subMenu) {
        const self = this
        const nested = subMenu.dataset.navSubmenuNested === '1'

        if (self.state.isMobile || nested) return

        const tail = subMenu.querySelector(self.config.navSubMenuTail)

        gsap.set(subMenu, { x: 0 })
        if (tail) gsap.set(tail, { x: 0 })

        const rect = subMenu.getBoundingClientRect()
        const overflowRight = rect.right - (window.innerWidth - self.edgeGutter)
        const overflowLeft = self.edgeGutter - rect.left
        const shift = overflowRight > 0 ? -overflowRight : overflowLeft > 0 ? overflowLeft : 0

        if (shift === 0) return

        gsap.set(subMenu, { x: shift })

        // The tail is centred on the panel itself, not the trigger - shift it
        // the opposite way so it stays pointing at the trigger it belongs to,
        // clamped so it never pokes out past the (now offset) panel's edges.
        if (tail) {
            const tailHalf = tail.offsetWidth / 2
            const maxTailShift = rect.width / 2 - tailHalf - self.edgeGutter

            gsap.set(tail, { x: gsap.utils.clamp(-maxTailShift, maxTailShift, -shift) })
        }
    },

    setSubMenuOpen(trigger, item, subMenu, open) {
        const self = this

        subMenu.dataset.navOpen = open ? '1' : '0'
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false')

        const chevron = trigger.querySelector(self.config.navChevron)

        if (chevron) {
            const base = Number(chevron.dataset.navChevronBase || 0)

            gsap.to(chevron, { rotate: base + (open ? 180 : 0), duration: self.dur(0.2) })
        }

        // Set synchronously, not from a tween callback: the Tailwind class
        // doing this at rest on desktop is `lg:pointer-events-none` (a
        // distinct token from `pointer-events-none`, so classList add/remove
        // can't touch it) - an inline style wins regardless, and setting it
        // up front means a fast double-click can never race the animation.
        subMenu.style.pointerEvents = open ? 'auto' : ''

        if (self.state.isMobile) {
            // The accordion is hidden by collapsing height to 0, not opacity -
            // but every submenu still starts life at autoAlpha: 0 (that's what
            // hides the desktop flyout), so autoAlpha has to come back up here
            // too or the content stays invisible once height opens back up.
            gsap.to(subMenu, {
                height: open ? 'auto' : 0,
                autoAlpha: open ? 1 : 0,
                duration: self.dur(0.3),
                ease: 'power2.out',
            })
        } else {
            gsap.to(subMenu, {
                autoAlpha: open ? 1 : 0,
                y: open ? 0 : -8,
                duration: self.dur(0.2),
            })
        }

        self.setSubMenuTabIndexes(subMenu, open)

        // Only ever turn the dimmer ON from here - closing it is closeSubMenus'
        // job (it knows whether another submenu is about to take this one's
        // place). Closing it here too raced that: switching directly between
        // two dropdowns closes the old one (open: false) a tick before the
        // new one opens, and toggling the shared backdrop off then straight
        // back on flashed the hero content through underneath.
        if (!self.state.isMobile && open) {
            self.setBackdropOpen(true)
        }
    },

    // Closes every open submenu except `keep` and anything on its own branch.
    closeSubMenus(keep = null) {
        const self = this

        self.elements.navTriggers.forEach((trigger) => {
            const item = trigger.closest(self.config.navItem)
            const subMenu = self.getSubMenu(trigger)

            if (!subMenu || subMenu.dataset.navOpen !== '1') return
            if (keep && (item.contains(keep) || keep.contains(item))) return

            self.setSubMenuOpen(trigger, item, subMenu, false)
        })

        if (!keep && !self.state.menuOpen) {
            self.setBackdropOpen(false)
        }
    },

    setBackdropOpen(open) {
        const self = this

        if (!self.elements.navBackdrop) return

        gsap.to(self.elements.navBackdrop, { autoAlpha: open ? 1 : 0, duration: self.dur(0.3) })

        if (open) {
            self.elements.navBackdrop.classList.remove('pointer-events-none')
            self.elements.navBackdrop.style.backdropFilter = 'blur(20px)'
        } else {
            self.elements.navBackdrop.classList.add('pointer-events-none')

            gsap.delayedCall(self.dur(0.3), () => {
                if (!self.hasOpenSubMenu() && !self.state.menuOpen) {
                    self.elements.navBackdrop.style.backdropFilter = ''
                }
            })
        }
    },

    // Anything inside a closed submenu is visually gone, so it must be out of
    // the tab order too.
    setSubMenuTabIndexes(subMenu, isFocusable) {
        subMenu.querySelectorAll('a, button').forEach((el) => {
            el.tabIndex = isFocusable ? 0 : -1
        })
    },

    /* Reset
    //------------------*/

    resetMenuState() {
        const self = this

        self.state.menuOpen = false
        self.elements.navMenuBtn?.setAttribute('aria-expanded', 'false')

        gsap.set(self.elements.navPanel, { autoAlpha: 0, y: -8 })
        self.elements.navPanel?.classList.add('pointer-events-none')
        self.panelTimeline?.kill()

        gsap.set(self.elements.navMenuIconOpen, { autoAlpha: 1, rotate: 0 })
        gsap.set(self.elements.navMenuIconClose, { autoAlpha: 0, rotate: -90 })

        // Crossing the breakpoint force-closes the panel without going
        // through setMenuOpen(false), so its own inert cleanup never runs -
        // left uncleared, the whole rest of the page would stay
        // unreachable by keyboard/screen reader after resizing to desktop.
        self.setBodyInert(false)

        enableScroll()
        self.closeSubMenus()
        self.updateTheme()
        self.applyScrolledLook()

        if (self.elements.navPanel) self.elements.navPanel.scrollTop = 0
    },

    /* Backdrop
    //------------------*/

    handleBackdropClick() {
        const self = this

        self.elements.navBackdrop?.addEventListener('click', () => {
            self.dismissAll()
        })
    },

    /* Sliding hover/focus indicator
    //------------------*/

    handleIndicator() {
        const self = this

        if (!self.elements.navList || !self.elements.navIndicator) return

        const moveTo = (el) => {
            const listRect = self.elements.navList.getBoundingClientRect()
            const elRect = el.getBoundingClientRect()

            gsap.to(self.elements.navIndicator, {
                x: elRect.left - listRect.left,
                width: elRect.width,
                autoAlpha: 1,
                duration: self.dur(0.3),
                ease: 'power2.out',
            })
        }

        const hide = () => {
            gsap.to(self.elements.navIndicator, { autoAlpha: 0, duration: self.dur(0.2) })
        }

        self.elements.navTopLinks.forEach((link) => {
            link.addEventListener('mouseenter', () => moveTo(link))
            link.addEventListener('focus', () => moveTo(link))
        })

        self.elements.navList.addEventListener('mouseleave', hide)
        self.elements.navList.addEventListener('focusout', (e) => {
            if (!self.elements.navList.contains(e.relatedTarget)) hide()
        })
    },

    // A desktop flyout left open has no mouse-driven close once the visitor
    // moves on by keyboard instead - clicking away closes it (handleOutsideClick),
    // but Tabbing past its last link and on to the next top-level item
    // doesn't, so it'd sit open and stale over whatever's rendered beneath
    // it. Closes it the moment focus leaves the desktop list entirely,
    // mirroring the sliding indicator's own focusout check just above.
    handleSubMenuFocusOut() {
        const self = this

        if (!self.elements.navList) return

        self.elements.navList.addEventListener('focusout', (e) => {
            if (self.state.isMobile) return
            if (self.elements.navList.contains(e.relatedTarget)) return

            self.closeSubMenus()
        })
    },

    /* Dismissal
    //------------------*/

    // Shared by Escape and the backdrop click: both dismiss whatever's open
    // without the visitor having clicked a new focus target of their own (an
    // outside click, by contrast, already moved focus wherever they clicked,
    // so handleOutsideClick leaves it alone) - so both restore focus to
    // whichever trigger opened what's being closed, rather than leaving
    // keyboard focus stranded on the backdrop or nowhere at all.
    dismissAll() {
        const self = this
        const openTrigger = [...self.elements.navTriggers].find((trigger) => {
            const subMenu = self.getSubMenu(trigger)

            return subMenu?.dataset.navOpen === '1'
        })
        const wasMenuOpen = self.state.menuOpen

        self.closeSubMenus()
        self.setMenuOpen(false)

        if (wasMenuOpen) {
            self.elements.navMenuBtn?.focus()
        } else if (openTrigger) {
            openTrigger.focus()
        }
    },

    handleKeydown() {
        const self = this

        document.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return

            self.dismissAll()
        })
    },

    handleOutsideClick() {
        const self = this

        document.addEventListener('click', (e) => {
            if (self.elements.nav.contains(e.target)) return

            self.closeSubMenus()
            self.setMenuOpen(false)
        })
    },
}

nav.init()

export default nav
